import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import AddIcon from '@mui/icons-material/Add';
import MainCard from 'components/MainCard';
import useGet from 'hooks/useGet';
import apiClient from 'utils/apiClient';
import { useAuth } from 'pages/AuthProvider';

const formatDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleString();
};

const discountLabel = (sale) => {
  if (!sale) return '-';
  if (sale.discountType === 'percentage') return `${sale.discountValue}%`;
  return Number(sale.discountValue || 0).toFixed(2);
};

const applyOnLabel = {
  all: 'All',
  products: 'Products',
  categories: 'Categories',
  subcategories: 'Subcategories'
};

export default function SaleList() {
  const navigate = useNavigate();
  const { canAccess } = useAuth();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [updatingId, setUpdatingId] = useState('');
  const [actionError, setActionError] = useState('');

  const endpoint = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page + 1));
    params.set('limit', String(rowsPerPage));
    if (search.trim()) params.set('q', search.trim());
    if (statusFilter !== 'all') params.set('isActive', String(statusFilter === 'active'));
    return `/api/sales/admin?${params.toString()}`;
  }, [page, rowsPerPage, search, statusFilter]);

  const { data, loading, error, mutate } = useGet(endpoint);
  const sales = data?.sales || [];

  const handleToggleActive = async (sale) => {
    const id = sale?._id || sale?.id;
    if (!id) return;

    setActionError('');
    setUpdatingId(id);
    try {
      await apiClient.patch(`/api/sales/admin/${id}/active`);
      await mutate();
    } catch (err) {
      setActionError(err?.response?.data?.message || 'Failed to update sale status.');
    } finally {
      setUpdatingId('');
    }
  };

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
        <Typography variant="h4">Sales</Typography>
        {canAccess('sales', 'create') && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/sales/new')}>
            Create Sale
          </Button>
        )}
      </Stack>

      <MainCard>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            fullWidth
            size="small"
            label="Search by name or description"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
          />
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </Stack>
      </MainCard>

      {actionError && <Alert severity="error">{actionError}</Alert>}
      {error && <Alert severity="error">Failed to load sales.</Alert>}

      <MainCard title={`Sale List (${data?.total || sales.length || 0})`}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Applies To</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Valid Until</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7}>Loading sales...</TableCell>
                </TableRow>
              ) : sales.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No sales found.
                  </TableCell>
                </TableRow>
              ) : (
                sales.map((sale) => {
                  const saleId = sale?._id || sale?.id;
                  const isActive = Boolean(sale?.isActive);
                  const isStackable = Boolean(sale?.isStackable);

                  return (
                    <TableRow key={saleId} hover>
                      <TableCell>{sale.name || '-'}</TableCell>
                      <TableCell>{discountLabel(sale)}</TableCell>
                      <TableCell>{applyOnLabel[sale.applyOn] || '-'}</TableCell>
                      <TableCell>{Number(sale.priority || 0)}</TableCell>
                      <TableCell>{formatDate(sale.validUntil)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.75}>
                          <Chip size="small" label={isActive ? 'Active' : 'Inactive'} color={isActive ? 'success' : 'default'} />
                          {isStackable ? <Chip size="small" label="Stackable" color="info" variant="outlined" /> : null}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
                          {canAccess('sales', 'edit') && (
                            <Tooltip title="Edit sale">
                              <IconButton color="primary" size="small" onClick={() => navigate(`/sales/${saleId}/edit`)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canAccess('sales', 'edit') && (
                            <Tooltip title={isActive ? 'Deactivate sale' : 'Activate sale'}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  disabled={updatingId === saleId}
                                  onClick={() => handleToggleActive(sale)}
                                >
                                  <SyncIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={Number(data?.total || 0)}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </MainCard>
    </Stack>
  );
}
